import { useState, useEffect, useMemo, useRef } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import type { CompanyState } from '../../../../types/company';
import type { Product } from '../../../../types/tycoon';
import type { PlayerTask, CompletionCheckType, PlayerTaskData } from '../../../../types/playerTasks';
import playerTasksData from '../../../../data/playerTasks.json' with { type: 'json' };

interface TaskListSectionProps {
  companyState: CompanyState;
  products?: Product[];
}

interface Task extends PlayerTask {
  completed: boolean;
  completedDate?: { year: number; month: number; day: number };
  isNew?: boolean;
}

// Type-safe task data
const typedTaskData = playerTasksData as PlayerTaskData;

export function TaskListSection({ companyState, products = [] }: TaskListSectionProps) {
  const [completedTasks, setCompletedTasks] = useState<Map<string, { year: number; month: number; day: number }>>(new Map());
  const [displayedTasks, setDisplayedTasks] = useState<Task[]>([]);
  const prevTasksRef = useRef<Task[]>([]);
  
  // Calculate months difference between two dates
  const monthsDifference = (date1: { year: number; month: number; day: number }, date2: { year: number; month: number; day: number }) => {
    return (date1.year - date2.year) * 12 + (date1.month - date2.month);
  };

  // Check if a task is completed based on its completion check
  const isTaskCompleted = (task: PlayerTask): boolean => {
    const check = task.completionCheck;
    
    switch (check.type as CompletionCheckType) {
      case 'hasEmployees':
        return Boolean(companyState.employees && companyState.employees.length > 0);
      
      case 'hasProducts':
        return products.length > 0;
      
      case 'hasReleasedProducts':
        return products.some(p => p.status === 'released');

      case 'hasResearch':
        return Boolean(
          (companyState.research?.activeResearch && companyState.research.activeResearch.length > 0) || 
          (companyState.research?.unlockedNodes && companyState.research.unlockedNodes.length > 0)
        );
      
      case 'releasedProductCount':
        return products.filter(p => p.status === 'released').length >= (check.value as number);
      
      case 'monthlyProfit':
        return (companyState.dailyStats.profit * 30) >= (check.value as number);
      
      case 'totalRevenue':
        // This would need to be tracked in CompanyState
        return false; // TODO: Implement when total revenue tracking is added
      
      case 'marketShare':
        return companyState.marketShare >= (check.value as number);
      
      case 'unlockedResearchCount':
        return (companyState.research?.unlockedNodes?.length || 0) >= (check.value as number);
      
      case 'employeeCount':
        return (companyState.employees?.length || 0) >= (check.value as number);
      
      case 'productQuality':
        // Check if any released product has quality >= value
        // Note: quality might need to be added to ProductMetadata
        return products.some(p =>
          p.status === 'released' &&
          p.techScore >= (check.value as number) // Using techScore as proxy for quality
        );
      
      case 'productionEfficiency':
        // This would need to be tracked
        return false; // TODO: Implement when production efficiency is tracked
      
      case 'productTechScore':
        return products.some(p =>
          p.status === 'released' &&
          p.techScore >= (check.value as number)
        );
      
      default:
        return false;
    }
  };

  // Get all active tasks (not hidden by time)
  const activeTasks = useMemo(() => {
    const tasks: Task[] = [];
    const currentDate = { year: companyState.year, month: companyState.month, day: companyState.day };
    
    // Add tutorial tasks
    const tutorialTasks = typedTaskData.tasks.tutorial.map(task => {
      const completed = isTaskCompleted(task);
      const completedDate = completed && !completedTasks.has(task.id) ? currentDate : completedTasks.get(task.id);
      
      // Update completed tasks tracking
      if (completed && !completedTasks.has(task.id)) {
        setCompletedTasks(prev => new Map(prev).set(task.id, currentDate));
      }
      
      return {
        ...task,
        completed,
        completedDate
      };
    });
    
    // Filter out tasks completed more than 1 month ago
    const visibleTutorialTasks = tutorialTasks.filter(task => {
      if (!task.completed || !task.completedDate) return true;
      return monthsDifference(currentDate, task.completedDate) < 1;
    });
    
    tasks.push(...visibleTutorialTasks);
    
    // Check if all tutorial tasks are completed (not just visible)
    const allTutorialComplete = tutorialTasks.every(t => t.completed);
    
    // Add advanced tasks if prerequisites are met
    if (allTutorialComplete) {
      const advancedTasks = typedTaskData.tasks.advanced
        .filter(task => !task.prerequisite || task.prerequisite === 'allTutorialComplete')
        .map(task => {
          const completed = isTaskCompleted(task);
          const completedDate = completed && !completedTasks.has(task.id) ? currentDate : completedTasks.get(task.id);
          
          if (completed && !completedTasks.has(task.id)) {
            setCompletedTasks(prev => new Map(prev).set(task.id, currentDate));
          }
          
          return {
            ...task,
            completed,
            completedDate
          };
        });
      
      // Filter out tasks completed more than 1 month ago
      const visibleAdvancedTasks = advancedTasks.filter(task => {
        if (!task.completed || !task.completedDate) return true;
        return monthsDifference(currentDate, task.completedDate) < 1;
      });
      
      tasks.push(...visibleAdvancedTasks);
    }
    
    return tasks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyState, products, completedTasks]); // isTaskCompleted is a stable function, not needed in deps

  // Update displayed tasks with animation tracking
  useEffect(() => {
    const newTasks = activeTasks.filter(task => 
      !prevTasksRef.current.some(prevTask => prevTask.id === task.id)
    );
    
    const updatedTasks = activeTasks.map(task => ({
      ...task,
      isNew: newTasks.some(newTask => newTask.id === task.id)
    }));
    
    setDisplayedTasks(updatedTasks);
    prevTasksRef.current = activeTasks;
  }, [activeTasks]);

  // Show up to 4 tasks now that we have more space
  const visibleTasks = displayedTasks.slice(0, 4);

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 text-center font-bold text-sm text-theme-text-secondary border-b border-theme-border">
        <span>Task List</span>
      </div>
      <div className="pt-10 p-3 h-full overflow-y-auto scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        <style>
          {`
            @keyframes slideInBottom {
              from {
                transform: translateY(20px);
                opacity: 0;
              }
              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
            
            @keyframes fadeOutUp {
              from {
                transform: translateY(0);
                opacity: 0.7;
              }
              to {
                transform: translateY(-10px);
                opacity: 0;
              }
            }
            
            .task-new {
              animation: slideInBottom 0.5s ease-out forwards;
            }
            
            .task-fade-out {
              animation: fadeOutUp 0.5s ease-out forwards;
            }
          `}
        </style>
        {visibleTasks.map((task, index) => {
          const shouldFadeOut = task.completed && task.completedDate && 
            monthsDifference(
              { year: companyState.year, month: companyState.month, day: companyState.day },
              task.completedDate
            ) >= 1;
            
          return (
            <div 
              key={task.id} 
              className={`
                bg-theme-bg-content/80 rounded p-2 mb-2 transition-all duration-500 ease-out
                ${task.completed ? 'opacity-70' : ''}
                ${task.isNew ? 'task-new' : ''}
                ${shouldFadeOut ? 'task-fade-out' : ''}
              `}
              style={{
                animationDelay: task.isNew ? `${index * 100}ms` : '0ms'
              }}
            >
              <div className="flex items-start gap-2">
                {task.completed ? (
                  <CheckCircle2 className="w-4 h-4 text-theme-success mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 text-theme-text-muted mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <div className={`text-xs font-medium ${
                    task.completed ? 'line-through text-theme-text-muted' : 'text-theme-text-secondary'
                  }`}>
                    {task.text}
                  </div>
                  {!task.completed && task.hint && (
                    <div className="text-xs text-theme-text-muted mt-0.5">{task.hint}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        
        {visibleTasks.length === 0 && (
          <div className="text-xs text-theme-text-muted text-center mt-4">
            No active tasks
          </div>
        )}
      </div>
    </div>
  );
}